import React, { memo, useCallback, useMemo, useState } from 'react';
import { Box, styled, Typography, useTheme } from '@mui/material';
import { ICrumbAdditionalData, IDineshTicketOperations, IPolicySet } from '@tymely/atoms';
import { useCreateTicketCrumb, useIntentsQuery, useSelectedComment } from '@tymely/services';
import { useDebounce } from '@tymely/utils';
import { addProposedIntents, groupAndSortIntents, searchIntent } from '@tymely/utils/intents';

import { Loader } from '../Loader';
import { IntentsList } from './IntentsList';
import { SearchAddIntent } from './SearchAddIntent';
import { containsHebrew, switchHebrewToEnglish } from './QuertyMapping';

const Root = styled(Box)({
    position: 'relative',
});

export const StyledLoader = styled(Loader)(({ theme }) => ({
    position: 'absolute',
    top: theme.spacing(10),
    height: 'auto',
}));

export const Intents = memo(
    (props: {
        selectedIds: IPolicySet['id'][];
        disabled?: boolean;
        onIntentSelect?: (intent: IPolicySet, searchTerm?: string) => void;
    }) => {
        const selectedComment = useSelectedComment();
        const [search, setSearch] = useState('');
        const searchVal = useDebounce(search, 300) || '';
        const inferredIntents = selectedComment?.infer_data?.intents;
        const intents = useIntentsQuery();
        const createTicketCrumb = useCreateTicketCrumb();
        const theme = useTheme();

        const allIntentGroups = useMemo(
            () => groupAndSortIntents(addProposedIntents(intents.data ?? [], inferredIntents)),
            [intents.data, inferredIntents],
        );

        const intentGroups = useMemo(() => {
            if (!searchVal.length) {
                return allIntentGroups;
            }

            if (searchVal.length <= 1) {
                return [];
            }

            const additionalData: ICrumbAdditionalData = { search_term: searchVal, comment_id: selectedComment?.id };
            createTicketCrumb(IDineshTicketOperations.USER_SEARCHED_INTENT, additionalData);

            const searchTerm = containsHebrew(searchVal) ? switchHebrewToEnglish(searchVal) : searchVal;
            const foundIntents = addProposedIntents(searchIntent(intents.data ?? [], searchTerm), inferredIntents);
            return groupAndSortIntents(foundIntents);
        }, [searchVal, allIntentGroups, inferredIntents, selectedComment]);

        const onIntentSelect = useCallback(
            (intent: IPolicySet) => {
                props.onIntentSelect?.(intent, searchVal);
            },
            [searchVal, props.onIntentSelect],
        );
        if (intents.isFetching) {
            return <Typography color="gray">Loading...</Typography>;
        }

        return (
            <Root sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ p: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
                    <Typography variant="h5" fontWeight={500} sx={{ mb: 1 }}>
                        Intent Selection
                    </Typography>
                    <SearchAddIntent
                        disabled={!!props.disabled}
                        search={search}
                        onSearch={setSearch}
                        onAdd={() => setSearch('')}
                    />
                </Box>
                <IntentsList
                    disabled={props.disabled}
                    expanded={Boolean(searchVal)}
                    intentsGrouped={intentGroups}
                    selectedIds={props.selectedIds}
                    onIntentClick={onIntentSelect}
                />
            </Root>
        );
    },
);

Intents.displayName = 'Intents';
